import React, { useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Button from '../components/Button';
import Select from '../components/Select';
import PageHeader from '../components/PageHeader';
import Table from '../components/Table';
import { getBonus } from '../api/Functions/marketing';
import MiniTable from '../components/MinTable';
import Input from '../components/Input';

export default function VendorReports() {
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('');

  // 🔹 Pobieranie danych
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetch('https://hs2api.razamit.pl/renewalsList');
        const data = await res.json();
        setRenewals(data);
      } catch (err) {
        console.error('Błąd pobierania:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // 🔹 Filtr roku / kwartału
  const filteredByDate = useMemo(() => {
    return renewals.filter(r => {
      if (r.price <= 0) return false;
      const d = new Date(String(r.renewal_date).replace(' ', 'T'));
      const year = d.getFullYear();
      const month = d.getMonth() + 1;

      if (year !== selectedYear) return false;
      if (selectedQuarter === 'all') return true;
      if (selectedQuarter === 'Q1') return month <= 3;
      if (selectedQuarter === 'Q2') return month > 3 && month <= 6;
      if (selectedQuarter === 'Q3') return month > 6 && month <= 9;
      if (selectedQuarter === 'Q4') return month > 9 && month <= 12;
      return true;
    });
  }, [renewals, selectedYear, selectedQuarter]);

  // 🔹 Grupowanie po vendorach
  const grouped = useMemo(() => {
    const result = {};

    filteredByDate.forEach(r => {
      const vendor = r.CompnyName || r.supplier || 'Nieznany';
      if (!result[vendor]) {
        result[vendor] = { items: [], count: 0, revenue: 0, bonus: 0, marketing: 0 };
      }
      result[vendor].items.push(r);
      result[vendor].count++;
      result[vendor].revenue += Number(r.price) || 0;
    });

    Object.keys(result).forEach(vendor => {
      const vendorData = result[vendor];
      const [marketing, sumaDlamarketingu, prowizjaVendor] = getBonus(vendorData.items);
      vendorData.marketing = Number(marketing);
      vendorData.bonus = Number(prowizjaVendor);
    });

    return Object.entries(result).map(([vendor, v]) => ({
      vendor,
      ...v,
      total: v.revenue - v.bonus ,
    }));
  }, [filteredByDate]);

  // 🔹 Filtrowanie po vendorze (tekstowe)
  const filteredRows = useMemo(() => {
    if (!vendorFilter) return grouped;
    return grouped.filter(v =>
      v.vendor.toLowerCase().includes(vendorFilter.toLowerCase())
    );
  }, [grouped, vendorFilter]);

  // 🔹 Kolumny do tabeli
 const columns = useMemo(() => [
    {
      field: 'vendor',
      headerName: 'Dystrybutor',
      flex: 1.5,
      renderCell: (params) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{params.row.vendor}</span>
        </div>
      )
    },
    {
      field: 'count',
      headerName: 'Liczba licencji',
      flex: 1,
      renderCell: (params) => (
        <span className="font-semibold text-gray-800">{params.row.count}</span>
      )
    },
    {
      field: 'revenue',
      headerName: 'Przychód (PLN)',
      flex: 1,
      renderCell: (params) => (
        <span>{Number(params.row.revenue).toFixed(2)} zł</span>
      )
    },
    {
      field: 'bonus',
      headerName: 'Do wypłaty (PLN)',
      flex: 1,
      renderCell: (params) => (
        <span className="text-green-600 font-medium">{Number(params.row.bonus).toFixed(2)} zł</span>
      )
    },
 
    {
      field: 'total',
      headerName: 'Razem (PLN)',
      flex: 1.2,
      renderCell: (params) => (
        <span className="font-semibold text-gray-900">{Number(params.row.total).toFixed(2)} zł</span>
      )
    },
       {
      field: 'marketing',
      headerName: 'Marketing (PLN)',
      flex: 1,
      renderCell: (params) => (
        <span className="text-blue-600 font-medium">+{Number(params.row.marketing).toFixed(2)} zł</span>
      )
    },
  ], []);
const summary = useMemo(() => {
  const totalRevenue = grouped.reduce((sum, v) => sum + v.revenue, 0);
  const totalBonus = grouped.reduce((sum, v) => sum + v.bonus, 0);
  const netto = totalRevenue - totalBonus;

  // dzielimy po połowie
  const perCompany = netto / 2;

  return [
    { company: 'Hanzo', amount: perCompany },
    { company: 'Razamit', amount: perCompany },
  ];
}, [grouped]);
  // 🔹 Eksport do PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text(`Raport rozliczenia  ${selectedYear}`, 14, 15);
    const tableData = grouped.map(v => [
      v.vendor,
      v.count,
      v.revenue.toFixed(2),
      v.bonus.toFixed(2),
      v.marketing.toFixed(2),
      v.total.toFixed(2),
    ]);
    autoTable(doc, {
      head: [['Vendor', 'Liczba licencji', 'Przychód', 'Prowizja', 'Marketing', 'Razem']],
      body: tableData,
      startY: 25,
    });
    doc.save(`Raport_${selectedYear}_${selectedQuarter}.pdf`);
  };

  return (
    <div>
      <PageHeader title="Raport rozliczeń" subtitle="Zbiorczy raport roczny i kwartalny" />

      {/* 🔹 Sekcja filtrów */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
           <Input
        label="Szukaj dystrybutora"
          type="text"
          placeholder="Nazwa dystrybutora..."
          size="small"
          value={vendorFilter}
          onChange={e => setVendorFilter(e.target.value)}
          className="border rounded p-2 text-sm"
        />
        <Select
        label="Rok"
        size="small"
          value={selectedYear}
          onChange={e => setSelectedYear(Number(e.target.value))}
          options={[2023, 2024, 2025].map(y => ({ value: y, label: y }))}
        />


        <Select
        label="Kwartał"
        size="small"
          value={selectedQuarter}
          onChange={e => setSelectedQuarter(e.target.value)}
          options={[
            { value: 'all', label: 'Cały rok' },
            { value: 'Q1', label: 'I kwartał' },
            { value: 'Q2', label: 'II kwartał' },
            { value: 'Q3', label: 'III kwartał' },
            { value: 'Q4', label: 'IV kwartał' },
          ]}
        />
     
        <Button onClick={exportToPDF}>Eksportuj do PDF</Button>
      </div>
<div className="mb-6">
  <MiniTable
  title="Podział zysku"
  columns={[
    { field: 'company', headerName: 'Firma' },
    {
      field: 'amount',
      headerName: 'Kwota (PLN)',
      align: 'right',
      renderCell: ({ value }) => (
        <span className="font-semibold text-gray-900">{value.toFixed(2)} zł</span>
      ),
      sortable: true,
    },
  ]}
  data={summary}
/>
</div>
      {/* 🔹 Tabela wynikowa */}
      <div className="overflow-x-auto bg-white rounded-lg shadow p-4">
        <Table columns={columns} data={filteredRows} loading={loading} />
      </div>
    </div>
  );
}
